/**
 * module-builder-form-layout.ts — dwukierunkowe tłumaczenie układu formularza widoku `form`
 * szablonu (`formConfig` — drzewo, pola adresowane SLUGIEM) ⇄ kształt sekcyjny buildera /
 * layoutu klienta (`{ sections }` — pola po `fieldId`). Hub Kreatora, Krok 2.
 *
 * KONTRAKT MAPOWANIA = nagłówek `apps/api/lib/module-builder/materialize-layouts.ts`
 * (translator instalacji z Kroku 1). Ten plik jest jego LUSTREM po stronie shared:
 *   - `templateFormConfigToClientSections` — ta sama logika co translator instalacji
 *     (adapter buildera w hubie ładuje istniejący formConfig do stanu buildera; mapa
 *     pól to identyczność slug→slug, bo hub nie zna bytów tenanta),
 *   - `clientSectionsToTemplateFormConfig` — DOKŁADNA ODWROTNOŚĆ: sekcje buildera →
 *     elementy `type='section'` z settings {title, columns, showHeader, showBorder,
 *     collapsible} i `children`; pola → `type='field'` z `fieldSlug` (NIGDY `fieldId`);
 *     szerokość pola wraca do `settings.width`; warunki widoczności z
 *     `settings.conditionalField/Operator/Value` wynoszone na top-level elementu
 *     (`conditionalFieldSlug/Operator/Value` — slugiem, bez tłumaczenia).
 *
 * Równoważność obu kierunków z translatorem instalacji utrwalają testy round-trip
 * w `apps/api/lib/module-builder/form-layout-roundtrip.test.ts`.
 *
 * Niuanse (świadome):
 *   - round-trip jest stabilny modulo `null ≡ brak klucza` (ta sama konwencja co odcisk
 *     z `fingerprint.ts`) — np. `settings.label: null` wraca jako brak warunku/nadpisania,
 *   - kolumny sekcji >3 są przycinane do 3 (kontrakt zna 1|2|3; translator instalacji
 *     i tak zbiłby 4–6 do defaultu 2 — przycięcie do 3 jest wierniejsze zamysłowi),
 *   - tytuł sekcji pusty/null → klucz `title` w settings pomijany (translator odda null).
 */

import type { ModuleTemplateFormConfig, ModuleTemplateFormElement } from './module-builder-views';

// ============================================================================
// Kształt sekcyjny buildera / layoutu klienta (wejście-wyjście konwerterów)
// ============================================================================

/** Element kształtu sekcyjnego — lustro `LayoutElement` buildera modułów custom. */
export interface ClientFormLayoutElement {
  id: string;
  type: string;
  /** Wypełnione TYLKO dla type='field' — w hubie to slug pola (mapa identycznościowa). */
  fieldId?: string;
  width: 1 | 2 | 3;
  settings: Record<string, unknown>;
  style?: Record<string, unknown>;
}

/**
 * Zakładka układu formularza (Krok 8) — rejestr w `layout_config.tabs`. Sekcje wskazują
 * zakładkę przez `tabId`; BRAK rejestru (klucz pominięty) = stary, płaski układ — dzięki
 * temu odciski (fingerprint) istniejących layoutów bez zakładek się nie zmieniają.
 */
export interface ClientFormLayoutTab {
  id: string;
  /** Slug do deep-linków (`?tab=...` u klienta) — stabilny przy zmianie tytułu. */
  slug: string;
  title: string;
}

/** Sekcja kształtu sekcyjnego — lustro `LayoutSection` buildera modułów custom. */
export interface ClientFormLayoutSection {
  id: string;
  title: string | null;
  columns: 1 | 2 | 3;
  showHeader: boolean;
  showBorder: boolean;
  collapsible: boolean;
  elements: ClientFormLayoutElement[];
  /** Zakładka, do której należy sekcja (Krok 8). Brak/nieznane = pierwsza zakładka. */
  tabId?: string | null;
}

/** Wejście odwrotności — builder dopuszcza szersze sekcje (kolumny 1–6, typowany styl). */
export interface ClientFormLayoutSectionInput {
  id: string;
  title: string | null;
  columns: number;
  showHeader: boolean;
  showBorder: boolean;
  collapsible: boolean;
  elements: ClientFormLayoutElementInput[];
  /** Zakładka, do której należy sekcja (Krok 8). Brak/nieznane = pierwsza zakładka. */
  tabId?: string | null;
}

/** Wejściowy element odwrotności — settings z indeksem, styl dowolnym obiektem. */
export interface ClientFormLayoutElementInput {
  id: string;
  type: string;
  fieldId?: string;
  width: 1 | 2 | 3;
  settings: Record<string, unknown>;
  style?: object;
}

/**
 * Pole w układzie nie mapuje się na znane pole (slug↔fieldId) — odpowiednik
 * `LayoutMaterializationError` z translatora instalacji.
 */
export class TemplateFormLayoutFieldError extends Error {
  constructor(public readonly fieldRef: string) {
    super(`Układ formularza wskazuje pole "${fieldRef}", którego nie ma wśród pól encji`);
    this.name = 'TemplateFormLayoutFieldError';
  }
}

// ============================================================================
// formConfig → sections (lustro translatora instalacji — Krok 1)
// ============================================================================

function asWidth(value: unknown, fallback: 1 | 2 | 3): 1 | 2 | 3 {
  return value === 1 || value === 2 || value === 3 ? value : fallback;
}

function translateElement(
  element: ModuleTemplateFormElement,
  fieldIdBySlug: ReadonlyMap<string, string>,
): ClientFormLayoutElement {
  const { width: settingsWidth, ...restSettings } = element.settings ?? {};
  const settings: Record<string, unknown> = { ...restSettings };

  if (element.conditionalFieldSlug !== undefined) {
    settings.conditionalField = element.conditionalFieldSlug;
  }
  if (element.conditionalOperator !== undefined) {
    settings.conditionalOperator = element.conditionalOperator;
  }
  if (element.conditionalValue !== undefined) {
    settings.conditionalValue = element.conditionalValue;
  }

  const out: ClientFormLayoutElement = {
    id: element.id,
    type: element.type,
    width: asWidth(settingsWidth, 1),
    settings,
  };
  if (element.style) out.style = element.style;

  if (element.type === 'field') {
    // Preferuj fieldSlug (standardowy kontrakt szablonu) → szukaj w mapie.
    // Fallback: fieldId przekazany wprost (layouty syntetyzowane z definicji kartoteki
    // przez buildDefaultLayoutConfig używają fieldId zamiast fieldSlug — dla rdzeniowych
    // pól kartoteki fieldId === slug === field.id w palecie buildera).
    let fieldId = element.fieldSlug ? fieldIdBySlug.get(element.fieldSlug) : undefined;
    if (!fieldId && element.fieldId) fieldId = element.fieldId;
    if (!fieldId) {
      throw new TemplateFormLayoutFieldError(element.fieldSlug ?? element.fieldId ?? '(brak)');
    }
    out.fieldId = fieldId;
  }

  return out;
}

/** Czy element ma strukturę kontenera (cokolwiek zagnieżdżonego)? */
function hasNestedChildren(element: ModuleTemplateFormElement): boolean {
  return Boolean(
    element.children?.length ||
    element.tabs?.length ||
    element.accordionItems?.length ||
    element.steps?.length,
  );
}

/** Spłaszcza poddrzewo do listy liści w kolejności dokumentowej (jak translator instalacji). */
function flattenLeaves(elements: ModuleTemplateFormElement[]): ModuleTemplateFormElement[] {
  const out: ModuleTemplateFormElement[] = [];
  for (const el of elements) {
    if (!hasNestedChildren(el)) {
      out.push(el);
      continue;
    }
    out.push(...flattenLeaves(el.children ?? []));
    for (const tab of el.tabs ?? []) out.push(...flattenLeaves(tab.children));
    for (const item of el.accordionItems ?? []) out.push(...flattenLeaves(item.children));
    for (const step of el.steps ?? []) out.push(...flattenLeaves(step.children));
  }
  return out;
}

function sectionFromElement(
  element: ModuleTemplateFormElement,
  fieldIdBySlug: ReadonlyMap<string, string>,
  tabId?: string,
): ClientFormLayoutSection {
  const settings = element.settings ?? {};
  return {
    id: element.id,
    title: typeof settings.title === 'string' && settings.title !== '' ? settings.title : null,
    columns: asWidth(settings.columns, 2),
    showHeader: typeof settings.showHeader === 'boolean' ? settings.showHeader : true,
    showBorder: typeof settings.showBorder === 'boolean' ? settings.showBorder : false,
    collapsible: typeof settings.collapsible === 'boolean' ? settings.collapsible : false,
    elements: flattenLeaves(element.children ?? []).map((child) =>
      translateElement(child, fieldIdBySlug),
    ),
    ...(tabId !== undefined ? { tabId } : {}),
  };
}

/**
 * Tłumaczy układ formularza szablonu (drzewo, pola slugiem) na kształt sekcyjny buildera
 * (pola po fieldId). Mapa identycznościowa slug→slug = stan buildera w hubie.
 * Rzuca `TemplateFormLayoutFieldError` przy nieznanym slugu pola.
 *
 * Zakładki (Krok 8): element ROOT `type='tabs'` rozkłada się na rejestr `tabs` + sekcje
 * jego dzieci ze wskazaniem `tabId`. Kontenery `tabs` zagnieżdżone GŁĘBIEJ (w sekcji)
 * nadal się spłaszczają (runtime sekcji jest płaski). Brak elementu tabs → `tabs: []`.
 */
export function templateFormConfigToClientSections(
  formConfig: ModuleTemplateFormConfig,
  fieldIdBySlug: ReadonlyMap<string, string>,
): { sections: ClientFormLayoutSection[]; tabs: ClientFormLayoutTab[] } {
  const sections: ClientFormLayoutSection[] = [];
  const tabs: ClientFormLayoutTab[] = [];
  let pendingLoose: ModuleTemplateFormElement[] = [];

  const flushLoose = (tabId?: string) => {
    if (pendingLoose.length === 0) return;
    sections.push({
      id: crypto.randomUUID(),
      title: null,
      columns: 2,
      showHeader: true,
      showBorder: false,
      collapsible: false,
      elements: flattenLeaves(pendingLoose).map((el) => translateElement(el, fieldIdBySlug)),
      ...(tabId !== undefined ? { tabId } : {}),
    });
    pendingLoose = [];
  };

  /** Dzieci zakładki przetwarzamy jak root (sekcje + luźne ciągi), ze wskazaniem tabId. */
  const processTabChildren = (children: ModuleTemplateFormElement[], tabId: string) => {
    for (const child of children) {
      if (child.type === 'section') {
        flushLoose(tabId);
        sections.push(sectionFromElement(child, fieldIdBySlug, tabId));
      } else {
        pendingLoose.push(child);
      }
    }
    flushLoose(tabId);
  };

  const elements = Array.isArray(formConfig.elements) ? formConfig.elements : [];
  for (const element of elements) {
    if (element.type === 'tabs' && element.tabs && element.tabs.length > 0) {
      flushLoose();
      const ordered = [...element.tabs].sort(
        (a, b) =>
          (a.sortOrder ?? element.tabs!.indexOf(a)) - (b.sortOrder ?? element.tabs!.indexOf(b)),
      );
      for (const tab of ordered) {
        tabs.push({ id: tab.id, slug: tab.slug, title: tab.title });
        processTabChildren(tab.children ?? [], tab.id);
      }
    } else if (element.type === 'section') {
      flushLoose();
      sections.push(sectionFromElement(element, fieldIdBySlug));
    } else {
      pendingLoose.push(element);
    }
  }
  flushLoose();

  return { sections, tabs };
}

// ============================================================================
// sections → formConfig (ODWROTNOŚĆ — zapis z huba)
// ============================================================================

/** Kolumny sekcji w kontrakcie to 1|2|3 — szersze siatki buildera przycinamy do 3. */
function clampSectionColumns(columns: number): 1 | 2 | 3 {
  if (columns <= 1) return 1;
  if (columns === 2) return 2;
  return 3;
}

function templateElementFromClient(
  el: ClientFormLayoutElementInput,
  fieldSlugById: ReadonlyMap<string, string>,
): ModuleTemplateFormElement {
  const {
    conditionalField,
    conditionalOperator,
    conditionalValue,
    width: _strayWidth,
    ...restSettings
  } = el.settings;

  const out: ModuleTemplateFormElement = {
    id: el.id,
    type: el.type,
    settings: { ...restSettings, width: el.width },
  };
  if (el.style && Object.keys(el.style).length > 0) {
    out.style = el.style as Record<string, unknown>;
  }

  if (el.type === 'field') {
    const slug = el.fieldId ? fieldSlugById.get(el.fieldId) : undefined;
    if (!slug) {
      throw new TemplateFormLayoutFieldError(el.fieldId ?? '(brak)');
    }
    out.fieldSlug = slug;
  }

  // Warunek widoczności: builder trzyma go w settings (slugiem) — kontrakt na top-level.
  // Brak/null/'' = brak warunku → klucze pomijamy (null ≡ brak klucza, jak w odcisku).
  if (typeof conditionalField === 'string' && conditionalField !== '') {
    out.conditionalFieldSlug = conditionalField;
    if (typeof conditionalOperator === 'string' && conditionalOperator !== '') {
      out.conditionalOperator = conditionalOperator;
    }
    if (conditionalValue !== undefined && conditionalValue !== null) {
      out.conditionalValue = conditionalValue;
    }
  }

  return out;
}

/**
 * ODWROTNOŚĆ translatora instalacji: stan buildera (sekcje, pola po fieldId) → `formConfig`
 * widoku `form` szablonu (drzewo, pola slugiem). Mapa identycznościowa slug→slug = hub.
 * Rzuca `TemplateFormLayoutFieldError` przy fieldId bez mapowania na slug.
 *
 * Zakładki (Krok 8): niepusty rejestr `tabs` → root z JEDNYM elementem `type='tabs'`
 * (stałe id `tabs_root` — deterministyczny zapis), sekcje rozdzielone po `tabId`
 * (nieznany/brak → pierwsza zakładka). Pusty/pominięty rejestr → płaski układ jak dotąd.
 */
export function clientSectionsToTemplateFormConfig(
  sections: readonly ClientFormLayoutSectionInput[],
  fieldSlugById: ReadonlyMap<string, string>,
  tabs?: readonly ClientFormLayoutTab[],
): ModuleTemplateFormConfig {
  const sectionToElement = (section: ClientFormLayoutSectionInput): ModuleTemplateFormElement => ({
    id: section.id,
    type: 'section',
    settings: {
      ...(section.title !== null && section.title !== '' ? { title: section.title } : {}),
      columns: clampSectionColumns(section.columns),
      showHeader: section.showHeader,
      showBorder: section.showBorder,
      collapsible: section.collapsible,
    },
    children: section.elements.map((el) => templateElementFromClient(el, fieldSlugById)),
  });

  if (!tabs || tabs.length === 0) {
    return { elements: sections.map(sectionToElement) };
  }

  const byTab = new Map<string, ModuleTemplateFormElement[]>(tabs.map((t) => [t.id, []]));
  const firstTabId = tabs[0]!.id;
  for (const section of sections) {
    const tabId = section.tabId && byTab.has(section.tabId) ? section.tabId : firstTabId;
    byTab.get(tabId)!.push(sectionToElement(section));
  }

  return {
    elements: [
      {
        id: 'tabs_root',
        type: 'tabs',
        tabs: tabs.map((tab) => ({
          id: tab.id,
          slug: tab.slug,
          title: tab.title,
          children: byTab.get(tab.id) ?? [],
        })),
      },
    ],
  };
}

/** Mapa identycznościowa slug→slug dla pól encji szablonu (stan buildera w hubie). */
export function identityFieldMap(fieldSlugs: readonly string[]): ReadonlyMap<string, string> {
  return new Map(fieldSlugs.map((slug) => [slug, slug]));
}
